<script setup lang="ts">
import {
  IAlarmDefrostTimeSettings,
  IAlarmEmptyMixerSettings,
  IAlarmInterruptionSettings,
  IAlarmLowLoadSettings,
  IAlarmOutsideRegularTimeSettings,
  IAlarmPeakHourSettings,
  IAlarmPowerFactorSettings,
  IAlarmNoDataSettings,
  IAlarmTemperatureSettings,
  IAlarmDoorOpenSettings,
  IAlarmBurntResistorSettings,
  IAlarmOvervoltageSettings
} from '@/interfaces/api/sensor';
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time';
import { computed } from 'vue';

    
const props = defineProps<{
    label: string
    settings: IAlarmInterruptionSettings |
     IAlarmLowLoadSettings |
     IAlarmPeakHourSettings |
     IAlarmDefrostTimeSettings |
     IAlarmEmptyMixerSettings |
     IAlarmOutsideRegularTimeSettings |
     IAlarmPowerFactorSettings |
     IAlarmNoDataSettings |
     IAlarmTemperatureSettings |
     IAlarmDoorOpenSettings |
     IAlarmOvervoltageSettings
}>()

const interruption_settings = computed(() => {
  const settings = props.settings as IAlarmInterruptionSettings;
  return [
    {
      label: 'alarms.min_current',
      value: settings?.interrupt_current_setpoint ?? '',
    },
    {
      label: 'alarms.time_min_current',
      value: settings?.interrupt_time_setpoint ?? '',
    },
    {
      label: 'alarms.min_debouncing',
      value: settings?.debouncing_time_setpoint ?? '',
    },
  ]
})

const low_load_settings = computed(() => {
  const settings = props.settings as IAlarmLowLoadSettings
  return [
    {
      label: 'alarms.max_current',
      value: settings?.low_load_max_current_setpoint ?? '',
    },
    {
      label: 'alarms.min_current',
      value: settings?.low_load_min_current_setpoint ?? '',
    },
    {
      label: 'alarms.low_load_duration',
      value: settings?.low_load_duration_setpoint ?? '',
    },
    {
      label: 'alarms.debounce_duration',
      value: settings?.low_load_debounce_duration_setpoint ?? '',
    },
  ]
})

const peak_hour_settings = computed(() => {
  const settings = props.settings as IAlarmPeakHourSettings
  return [
    {
      label: 'alarms.min_current',
      value: settings?.peak_hour_current_threshold ?? '',
    },
    {
      label: 'alarms.peak_hour.peak_hour_start_time',
      value: settings?.peak_hour_start_time ?? ''
        ? formatOutsideRegularUtcTimeToLocalHHmm(settings.peak_hour_start_time)
        : '',
    },
    {
      label: 'alarms.peak_hour.peak_hour_current_threshold',
      value: settings?.peak_hour_duration_minutes ?? '',
    },
    {
      label: 'alarms.peak_hour.peak_hour_min_percentage_interrupt',
      value: settings?.peak_hour_min_percentage_interrupt ?? '',
    }
  ]
})

const defrost_time_settings = computed(() => {
  const settings = props.settings as IAlarmDefrostTimeSettings
  return [
    {
      label: 'alarms.min_current',
      value: settings?.defrost_min_current_setpoint ?? '',
    },
    {
      label: 'alarms.max_current',
      value: settings?.defrost_max_current_setpoint ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_current_ratio_setpoint_max',
      value: settings?.defrost_current_ratio_setpoint_max ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_current_ratio_setpoint_min',
      value: settings?.defrost_current_ratio_setpoint_min ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_duration_setpoint',
      value: settings?.defrost_duration_setpoint ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_duration_error_margin',
      value: settings?.defrost_duration_error_margin ?? '',
    }
  ]
})

const burnt_resistor_settings = computed(() => {
  const settings = props.settings as IAlarmBurntResistorSettings
  return [
    {
      label: 'alarms.min_current',
      value: settings?.defrost_min_current_setpoint ?? '',
    },
    {
      label: 'alarms.max_current',
      value: settings?.defrost_max_current_setpoint ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_current_ratio_setpoint_max',
      value: settings?.defrost_current_ratio_setpoint_max ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_current_ratio_setpoint_min',
      value: settings?.defrost_current_ratio_setpoint_min ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_duration_setpoint',
      value: settings?.defrost_duration_setpoint ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_duration_error_margin',
      value: settings?.defrost_duration_error_margin ?? '',
    },
    {
      label: 'alarms.defrost_time.defrost_debounce_duration_setpoint',
      value: settings?.defrost_debounce_duration_setpoint ?? '',
    }
  ]
})

const empty_mixer_settings = computed(() => {
  const settings = props.settings as IAlarmEmptyMixerSettings
  return [
    {
      label: 'alarms.empty_mixer.multiplier',
      value: settings?.empty_mixer_multiplier ?? '',
    },
    {
      label: 'alarms.empty_mixer.variation_threshold',
      value: settings?.empty_mixer_variation_threshold ?? '',
    },
    {
      label: 'alarms.empty_mixer.activation_setpoint',
      value: settings?.empty_mixer_activation_setpoint ?? '',
    },
    {
      label: 'alarms.empty_mixer.min_peak_duration_minutes',
      value: settings?.empty_mixer_min_peak_duration_minutes ?? '',
    },
    {
      label: 'alarms.empty_mixer.time_limit_minutes',
      value: settings?.empty_mixer_time_limit_minutes ?? '',
    }
  ]
})

const outside_regular_time_settings = computed(() => {
  const settings = props.settings as IAlarmOutsideRegularTimeSettings
  return [
    {
      label: 'alarms.outside_regular_time.setpoint',
      value: settings?.outside_regular_time_setpoint ?? '',
    },
    {
      label: 'alarms.outside_regular_time.duration_setpoint',
      value: settings?.outside_regular_time_duration_setpoint ?? '',
    },
    {
      label: 'alarms.outside_regular_time.debounce_duration_setpoint',
      value: settings?.outside_regular_time_debounce_duration_setpoint ?? '',
    },
    {
      label: 'alarms.outside_regular_time.start_time',
      value: settings?.outside_regular_time_start_time ?? ''
        ? formatOutsideRegularUtcTimeToLocalHHmm(settings.outside_regular_time_start_time)
        : '',
    },
    {
      label: 'alarms.outside_regular_time.end_time',
      value: settings?.outside_regular_time_end_time ?? ''
        ? formatOutsideRegularUtcTimeToLocalHHmm(settings.outside_regular_time_end_time)
        : '',
    }
  ]
})

const power_factor_settings = computed(() => {
  const settings = props.settings as IAlarmPowerFactorSettings
  return [
    {
      label: 'alarms.power_factor.power_factor_setpoint',
      value: settings?.power_factor_setpoint ?? '',
    },
  ]
})

const no_data_settings = computed(() => {
  const settings = props.settings as IAlarmNoDataSettings
  return [
    {
      label: 'alarms.no_data.no_data_setpoint',
      value: settings?.no_data_duration_setpoint ?? '',
    },
  ]
})

const critical_temperature_settings = computed(() => {
  const settings = props.settings as IAlarmTemperatureSettings
  return [
    {
      label: 'alarms.critical_temperature.temperature_setpoint',
      value: settings?.temperature_setpoint ?? '',
    },
    {
      label: 'alarms.critical_temperature.temperature_duration_setpoint',
      value: settings?.temperature_duration_setpoint ?? '',
    },
    {
      label: 'alarms.critical_temperature.temperature_debounce_duration_setpoint',
      value: settings?.temperature_debounce_duration_setpoint ?? '',
    }
  ]
})

const door_open_settings = computed(() => {
  const settings = props.settings as IAlarmDoorOpenSettings
  return [
    {
      label: 'alarms.door_open.max_door_open_time_setpoint',
      value: settings?.max_door_open_time_setpoint ?? '',
    },
    {
      label: 'alarms.door_open.debounce_time_setpoint',
      value: settings?.debounce_time_setpoint ?? '',
    }
  ]
})

const overvoltage_settings = computed(() => {
  const settings = props.settings as IAlarmOvervoltageSettings
  return [
    {
      label: 'alarms.overvoltage.overvoltage_duration_setpoint',
      value: settings?.overvoltage_duration_setpoint ?? '',
    },
    {
      label: 'alarms.overvoltage.overvoltage_debounce_duration_setpoint',
      value: settings?.overvoltage_debounce_duration_setpoint ?? '',
    },
  ]
})

const settingsMap = {
  'Interrupção': interruption_settings,
  'Carga baixa': low_load_settings,
  'Ativação em horário de pico': peak_hour_settings,
  'Tempo de degelo': defrost_time_settings,
  'Resistência queimada': burnt_resistor_settings,
  'Misturador vazio': empty_mixer_settings,
  'Funcionamento fora do horário padrão': outside_regular_time_settings,
  'Fator de potência': power_factor_settings,
  'Ausência de dados': no_data_settings,
  'Temperatura crítica': critical_temperature_settings,
  'Abertura de porta': door_open_settings,
  'Sobretensão': overvoltage_settings,
}

const currentSettings = computed(() => settingsMap[props.label as keyof typeof settingsMap]?.value)

</script>

<template>
    <div class="space-y-0">
        <div v-for="(setting, index) in currentSettings" :key='index' class="flex justify-between items-center pt-1 border-b border-stroke pb-2 border-dashed">
            <span class="text-sm text-body font-medium">
                {{ $t(setting.label) }}
            </span>
            <span class="text-sm font-bold text-black font-mono">{{ setting.value }}</span>
        </div>
    </div>
</template>